import React, { useCallback, useEffect, useState } from 'react'
import { ThemeProvider } from '@mui/material/styles'
import { BrowserRouter } from 'react-router-dom'
import { useCookies } from 'react-cookie'
import { useDispatch, useSelector } from 'react-redux'
import { onChangeLanguage, refreshAccount, setLoggingin } from './actions'
import '../src/containers/Home/Home.css'
import Routes from './Routes'
import NavBar from './components/Nav/NavBar'
import Footer from './components/Footer/Footer'
import PendingSubscriptionPaymentPopUp from './components/PendingSubscriptionPaymentPopUp'
import './App.css'
import './components/ResponsivePlayer.css'
import { darkTheme } from './styles'
import CookiesBanner from './containers/CookiesBanner'
import { getCountryOfUser, updateTTLUserId } from './services'
import { CssBaseline } from '@mui/material'
import { IReducer } from './interface'
import VideoModal from './components/VideoModal'
import { updateAnalyticsCookies, updateFunctionalityCookies, updateMarketingCookies } from './utils/cookies'
import useCartState from './customHooks/useCartState'
import { ToastContainer } from 'react-toastify'
import { NotificationManager } from './functions/notifications'
import { defaultLang } from './constants'
import { loginWithTokenGQL } from './services/graphql/schemas/authentication'

interface AppContextInterface {
  isSmallScreen: boolean
  isFromUSCountry: boolean
}
type NotificationType = 'info' | 'success' | 'warning' | 'error'

export const ConfigContext = React.createContext<AppContextInterface>({
  isSmallScreen: false,
  isFromUSCountry: false
})

const App = () => {
  const [cookies, setCookie] = useCookies()
  const language = useSelector((state: IReducer) => state.changeLanguage)
  const handleIsMFCartLoaded = useCartState()
  const dispatch = useDispatch()
  const [isSmallScreen, setIsSmallScreen] = useState(window.matchMedia('(max-width: 1200px)').matches)

  const configValue = {
    isSmallScreen,
    isFromUSCountry: cookies.country_code === 'us'
  }

  const setCookieMC = () => {
    const MCCookies = window.location.href.split('?')[1]
    if (MCCookies) {
      // 5256000 two Months
      setCookie('MCSalesForce', MCCookies, { path: '/', maxAge: 5256000 })
    }
  }

  const initialAddNotification = useCallback((event: CustomEventInit) => {
    const { type, message } = event.detail
    NotificationManager[type as NotificationType](message as string | React.ReactElement)
  }, [])

  const refreshUser = useCallback(async () => {
    // If login is from forum don't refresh the user account
    const params = new URLSearchParams(window.location.search)
    if (params.has('sso') && params.has('sig')) {
      dispatch(setLoggingin(false))
      return
    }
    // Disable the login button while the user is being refreshed, which happens when the user access /login directly
    dispatch(setLoggingin(true))
    let username
    try {
      const data = await loginWithTokenGQL()
      username = data?.username ?? null
    } catch (error) {
      username = null
    }
    if (username) {
      try {
        await dispatch(refreshAccount(username, setCookie, cookies.popupDay))
      } catch (error) {
        console.error('refreshUser - ', error)
      }
    } else {
      dispatch(setLoggingin(false))
    }
  }, [dispatch, cookies.temporary_user_id, setCookie])

  useEffect(() => {
    if (cookies.country_code === undefined) {
      getCountryOfUser()
        .then((result) => {
          window.dispatchEvent(new CustomEvent('setCountry', { detail: { country_code: result } }))
          setCookie('country_code', result, { path: '/' })
        })
        .catch(() => {
          window.dispatchEvent(new CustomEvent('setCountry', { detail: { country_code: 'us' } }))
          setCookie('country_code', 'us', { path: '/' })
        })
    }
  }, [cookies.country_code, setCookie])

  useEffect(() => {
    // Initialize cookies
    updateFunctionalityCookies(cookies.functionalityCookies ? 'granted' : 'denied')
    updateMarketingCookies(cookies.marketingCookies ? 'granted' : 'denied')
    updateAnalyticsCookies(cookies.analysisCookies ? 'granted' : 'denied')

    // Refresh user session
    refreshUser()

    // Check if the user is accessing the website from a mobile device
    const handler = (e: any) => {
      setIsSmallScreen(e.matches)
    }
    setCookieMC()
    window.matchMedia('(max-width: 1200px)').addListener(handler)
  }, [])

  useEffect(() => {
    if (cookies.temporary_user_id === undefined) {
      const { temporary_user_id, date } = updateTTLUserId()
      setCookie('temporary_user_id', temporary_user_id, { expires: date, path: '/' })
    }
  }, [cookies.temporary_user_id, setCookie])

  useEffect(() => {
    if (cookies.language === undefined) {
      const routeLang = window.location.pathname.startsWith('/es') ? 'es' : 'en'
      setCookie('language', routeLang, { path: '/' })
      dispatch(onChangeLanguage(routeLang))
      if (defaultLang !== 'en') {
        window.location.replace(`/${defaultLang}`)
      }
    }
  }, [language.lang, cookies.functionalityCookies])

  useEffect(() => {
    window.addEventListener('sendNotification', initialAddNotification)
    window.addEventListener('onReadyMicrofrontendCart', handleIsMFCartLoaded)
    return () => {
      window.removeEventListener('sendNotification', initialAddNotification)
      window.removeEventListener('onReadyMicrofrontendCart', handleIsMFCartLoaded)
    }
  }, [initialAddNotification, handleIsMFCartLoaded, dispatch])

  return (
    <BrowserRouter>
      <ThemeProvider theme={darkTheme}>
        <ConfigContext.Provider value={configValue}>
          <CssBaseline>
            <div className='App'>
              <ToastContainer />
              <PendingSubscriptionPaymentPopUp />
              <NavBar />
              <Routes />
              <Footer />
              <CookiesBanner />
              <VideoModal />
            </div>
          </CssBaseline>
        </ConfigContext.Provider>
      </ThemeProvider>
    </BrowserRouter>
  )
}

export default App
